import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import {
  processingServicesSales,
  type ProcessingServicesSalesListItem,
} from '@/api/processingServicesSales';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'production.processing.service.manage';

export default function ProcessingServicesSalesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['processing-services-sales', orgId],
    queryFn: () =>
      processingServicesSales.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const fmtAmt = (v: string | null | undefined) =>
    v
      ? Number(v).toLocaleString('uk-UA', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '';

  const cols = useMemo<ColumnDef<ProcessingServicesSalesListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'customer_contractor_id',
        header: 'Замовник',
        sortValue: (r) => cName.get(r.customer_contractor_id) ?? '',
        cell: (r) => cName.get(r.customer_contractor_id) ?? '',
      },
      {
        key: 'service_amount',
        header: 'Сума послуги',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.service_amount),
        cell: (r) => fmtAmt(r.service_amount),
      },
      {
        key: 'total_amount',
        header: 'Разом з ПДВ',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmtAmt(r.total_amount),
      },
      {
        key: 'posted',
        header: 'Проведений',
        width: 140,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [cName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати Послуги переробки у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Послуги переробки (продаж)</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <button
            className="primary"
            style={{ marginLeft: 'auto' }}
            disabled={!canCreate}
            title={tooltipNew}
            onClick={() => navigate('/processing-services-sales/new')}
          >
            + Нова реалізація послуги
        </button>
      </div>
      <DataTable<ProcessingServicesSalesListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/processing-services-sales/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
